import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { getMaxUSDC } from "../../services/WizardServices";
import "./StepFour.css";
import { Pie, PieChart, ResponsiveContainer, Cell } from "recharts";
import CryptoIconWrapper from "../cryptoicons/CryptoIconWrapper";

const TOKEN_COLORS = {
  BTC: "#F7931A",
  ETH: "#627EEA",
  XRP: "#23292F",
  SOL: "#14F195",
  DOT: "#E6007A",
  ADA: "#0033AD",
  AVAX: "#E84142",
  LINK: "#2A5ADA",
  MATIC: "#8247E5",
  UNI: "#FF007A",
};

const getTokenColor = (tokenSymbol) => {
  if (!tokenSymbol) return "#6C5DD3";
  const upperSymbol = tokenSymbol.toUpperCase();
  return (
    TOKEN_COLORS[upperSymbol] ||
    `hsl(${Math.abs(
      upperSymbol
        .split("")
        .reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0) % 360
    )}, 70%, 45%)`
  );
};

const formatUSDC = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const HOUSE_COMMISSION = 2.5; // Fixed house commission value (in %)
const ECOSYSTEM_SUPPORT = 2.5; // Fixed ecosystem support value (in %)

const StepFour = ({ onNext, onBack, currentStep, selectedTokens }) => {
  const [usdcAmount, setUsdcAmount] = useState("");
  const [allocations, setAllocations] = useState({});
  const [totalAllocation, setTotalAllocation] = useState(0);
  const [inputError, setInputError] = useState("");
  const [maxUSDC, setMaxUSDC] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [allocationErrors, setAllocationErrors] = useState({});
  const [creatorCommission, setCreatorCommission] = useState(0); // Creator's commission (adjustable)
  const [pieData, setPieData] = useState([]);

  // Initialize allocations
  useEffect(() => {
    if (selectedTokens?.length) {
      const initialAllocations = {};
      selectedTokens.forEach((token) => {
        initialAllocations[token.symbol] = 0;
      });
      setAllocations(initialAllocations);
    }
  }, [selectedTokens]);

  // Calculate total allocation and validate
  useEffect(() => {
    const total = Object.values(allocations).reduce(
      (sum, value) => sum + value,
      0
    );

    if (total > 100) {
      // Scale down allocations proportionally
      const scale = 100 / total;
      const scaledAllocations = Object.fromEntries(
        Object.entries(allocations).map(([symbol, value]) => [
          symbol,
          Math.floor(value * scale),
        ])
      );
      setAllocations(scaledAllocations);
      return;
    }

    setTotalAllocation(total);
    validateAllocations(total);
  }, [allocations]);

  const validateAllocations = (total) => {
    const newErrors = {};
    if (total === 100) {
      Object.entries(allocations).forEach(([symbol, value]) => {
        if (value > 0 && value < 1) {
          newErrors[symbol] = "Minimum allocation is 1%";
        }
      });
    }
    setAllocationErrors(newErrors);
  };

  // Calculate USDC value for a given percentage
  const calculateUSDCValue = (percentage) => {
    if (!usdcAmount || percentage === 0) return 0;
    return (parseFloat(usdcAmount) * percentage) / 100;
  };

  const handleUSDCInput = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    if (value === "") {
      setUsdcAmount("");
      setInputError("");
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setInputError("Please enter a valid number");
    } else if (numValue <= 0) {
      setInputError("Amount must be greater than 0");
    } else if (numValue > maxUSDC) {
      setInputError(`Maximum amount is ${maxUSDC.toLocaleString()} USDC`);
    } else {
      setInputError("");
    }
    setUsdcAmount(value);
  };

  const getRemainingAllocation = (excludeSymbol) => {
    const currentTotal = Object.entries(allocations)
      .filter(([symbol]) => symbol !== excludeSymbol)
      .reduce((sum, [, value]) => sum + value, 0);

    const currentTokenAllocation = allocations[excludeSymbol] || 0;
    return Math.min(100, 100 - currentTotal + currentTokenAllocation);
  };

  const handleAllocationChange = (symbol, newValue) => {
    const maxAllowed = getRemainingAllocation(symbol);
    const clampedValue = Math.min(Math.max(0, newValue), maxAllowed);

    setAllocations((prev) => ({
      ...prev,
      [symbol]: clampedValue,
    }));
  };

  const handleMaxAllocation = (symbol) => {
    const maxAllowed = getRemainingAllocation(symbol);
    handleAllocationChange(symbol, maxAllowed);
  };

  // const calculateChartSegments = useMemo(() => {
  //   let currentAngle = 0;
  //   return Object.entries(allocations)
  //     .filter(([, percentage]) => percentage > 0)
  //     .map(([symbol, percentage]) => {
  //       const angle = (percentage / 100) * 360;
  //       const segment = {
  //         symbol,
  //         startAngle: currentAngle,
  //         angle,
  //       };
  //       currentAngle += angle;
  //       return segment;
  //     });
  // }, [allocations]);

  const isValid = useMemo(
    () =>
      totalAllocation === 100 &&
      usdcAmount &&
      !inputError &&
      Object.keys(allocationErrors).length === 0,
    [totalAllocation, usdcAmount, inputError, allocationErrors]
  );

  // Fetch max USDC
  useEffect(() => {
    getMaxUSDC()
      .then((amount) => {
        setMaxUSDC(amount);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch max USDC:", error);
        setInputError("Failed to fetch maximum USDC amount");
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const data = selectedTokens
      .filter((token) => allocations[token.symbol] > 0)
      .map((token) => ({
        name: token.symbol,
        value: allocations[token.symbol],
        color: getTokenColor(token.symbol),
      }));
    setPieData(data);
  }, [selectedTokens, allocations]);

  console.log(pieData);

  return (
    <div className="create-pool-container">
      {/* Header */}
      <div className="wizard-header">
        <div className="step-indicator">4</div>
        <div>
          <h2 className="wizard-title">Create a Pool</h2>
          <p className="wizard-description">
            Add funds, allocate them, and set your commission
          </p>
        </div>
      </div>

      <div className="pool-content">
        {/* Add Funds Section */}
        <div className="add-funds-section">
          <h3>Add Funds</h3>
          <p className="funds-description">
            Enter the amount of USDC you want to add to the pool
          </p>
          <div className="usdc-input-container">
            <div className={`usdc-input ${inputError ? "error" : ""}`}>
              <div className="usdc-icon">
                <CryptoIconWrapper name="usdc" size={32} />
              </div>
              <input
                type="text"
                value={usdcAmount}
                onChange={handleUSDCInput}
                placeholder={
                  isLoading
                    ? "Loading..."
                    : `Max ${maxUSDC.toLocaleString()} USDC`
                }
                className="max-usdc-input"
                disabled={isLoading}
              />
            </div>
            {inputError && <span className="error-message">{inputError}</span>}
          </div>
        </div>

        {/* Initialize Odds Section */}
        <div className="allocate-funds-section">
          <div className="allocation-header">
            <div>
              <h3>Initialize Odds</h3>
              <p className="allocation-description">
                {usdcAmount ? (
                  <>
                    Total: ${formatUSDC(usdcAmount)} USDC
                    {totalAllocation < 100 && (
                      <span className="remaining-allocation">
                        {` (${formatUSDC(
                          calculateUSDCValue(100 - totalAllocation)
                        )} USDC remaining)`}
                      </span>
                    )}
                  </>
                ) : (
                  "Enter USDC amount above to see token allocations"
                )}
              </p>
            </div>
            <button
              onClick={() => setAllocations({})}
              className="reset-button"
              type="button"
            >
              Reset Allocations
            </button>
          </div>

          <div className="allocation-container">
            <div className="sliders-container">
              {selectedTokens?.map((token) => {
                const tokenAllocation = allocations[token.symbol] || 0;
                const usdcValue = calculateUSDCValue(tokenAllocation);
                return (
                  <div key={token.symbol} className="flex gap-3">
                    <div className="flex w-fit flex-col gap-2 items-center">
                      <CryptoIconWrapper name={token.symbol.toLowerCase()} size={32} />
                      <span className="text-sm">{token.symbol}</span>
                    </div>
                    <div className="allocation-slider-container">
                      <div className="flex flex-col w-48 gap-3">
                        <span className="allocation-percentage">
                          {tokenAllocation}%
                        </span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={tokenAllocation}
                          onChange={(e) =>
                            handleAllocationChange(
                              token.symbol,
                              parseInt(e.target.value)
                            )
                          }
                          className="allocation-slider"
                          style={{
                            "--slider-percentage": `${tokenAllocation}%`,
                            "--slider-color": getTokenColor(token.symbol),
                          }}
                        />
                        <span className="allocation-usdc">
                          ${formatUSDC(usdcValue)} USDC
                        </span>
                      </div>
                      <button
                        onClick={() => handleMaxAllocation(token.symbol)}
                        className="max-button"
                        type="button"
                        disabled={getRemainingAllocation(token.symbol) === 0}
                      >
                        Max
                      </button>
                    </div>
                    {allocationErrors[token.symbol] && (
                      <span className="error-message">
                        {allocationErrors[token.symbol]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="allocation-chart-container">
              <div className="allocation-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      innerRadius="60%"
                      outerRadius="100%"
                      paddingAngle={4}
                      dataKey="value"
                      cornerRadius={4}
                      stroke="transparent"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-row gap-6 justify-center">
                {selectedTokens?.map((token) => (
                  <div key={token.symbol} className="flex gap-1 items-center">
                    <div
                      className="legend-color radius-full"
                      style={{ backgroundColor: getTokenColor(token.symbol) }}
                    />
                    <span className="text-sm opacity-80">{token.symbol}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Commission Section */}
        <div className="commission-section">
          <h3 className="commission-title">Race Proposer Share</h3>
          <div className="commission-container">
            <div className="commission-field">
              <label className="commission-label" htmlFor="creator-commission">
                Your Commission:
              </label>
              <input
                id="creator-commission"
                type="range"
                min="0"
                max="10"
                value={creatorCommission}
                onChange={(e) => setCreatorCommission(parseInt(e.target.value))}
                className="commission-slider"
                style={{
                  "--slider-percentage": `${creatorCommission * 10}%`,
                  "--slider-color": "#6C5DD3",
                }}
              />
              <span className="commission-value">{creatorCommission}%</span>
            </div>
            <div className="commission-field">
              <label className="commission-label">House Commission:</label>
              <span className="commission-value">{HOUSE_COMMISSION}%</span>
            </div>
            <div className="commission-field">
              <label className="commission-label">Ecosystem Support:</label>
              <span className="commission-value">{ECOSYSTEM_SUPPORT}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Button Container */}
      <div className="button-container">
        {currentStep > 1 && (
          <button onClick={onBack} className="back-button">
            Back
          </button>
        )}
        <button
          onClick={() => {
            if (isValid) {
              onNext({
                allocations,
                usdcAmount: parseFloat(usdcAmount),
                maxUSDC,
                creatorCommission,
                houseCommission: HOUSE_COMMISSION,
              });
            }
          }}
          className="review-button"
          disabled={!isValid || isLoading}
        >
          Review
        </button>
      </div>
    </div>
  );
};

StepFour.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  selectedTokens: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default StepFour;
